import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const USERS_COLLECTION = 'users';
const ACCOUNTS_COLLECTION = 'accounts';

/**
 * Load overhead data for the current user
 * @returns {Promise<Object>} Overhead data object
 */
export const loadOverheadData = async (userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to load overhead data');
    }

    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.overhead || getDefaultOverheadData();
    }
    
    return getDefaultOverheadData();
  } catch (error) {
    console.error('Error loading overhead data:', error);
    return getDefaultOverheadData();
  }
};

/**
 * Annual total for a quick-mode overhead entry ({ amount, frequency }).
 * Quick mode treats this single number as the company's whole (fixed) overhead.
 * Frequency is any of the calculator's periods: Weekly / Monthly / Quarterly / Annual.
 * @param {{amount:number|string, frequency:string}} quickOverhead
 * @returns {number} annual dollars (0 when unset/invalid)
 */
export const quickOverheadAnnual = (quickOverhead) => {
  const amount = parseFloat(quickOverhead?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  switch (quickOverhead?.frequency) {
    case 'Weekly': return amount * 52;
    case 'Quarterly': return amount * 4;
    case 'Annual': return amount;
    case 'Monthly':
    default: return amount * 12;
  }
};

/**
 * Switch the user to quick overhead mode and save a single fixed total.
 * Merges into the existing overhead doc so the detailed `expenses` breakdown is
 * preserved (unused while in quick mode, restored if they switch back).
 * @param {number} amount
 * @param {'Monthly'|'Annual'} frequency
 * @returns {Promise<void>}
 */
export const saveQuickOverhead = async (amount, frequency = 'Monthly', userId = null) => {
  await saveOverheadData({
    inputMode: 'quick',
    quickOverhead: { amount: Number(amount) || 0, frequency }
  }, userId);
};

/**
 * Save overhead data for the current user
 * @param {Object} overheadData - The overhead data to save
 * @returns {Promise<void>}
 */
export const saveOverheadData = async (overheadData, userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to save overhead data');
    }

    const userRef = doc(db, USERS_COLLECTION, uid);
    await setDoc(userRef, {
      overhead: {
        ...overheadData,
        lastUpdated: new Date().toISOString(),
        version: '1.0'
      }
    }, { merge: true });
  } catch (error) {
    console.error('Error saving overhead data:', error);
    throw error;
  }
};

/**
 * Get default overhead data structure
 * @returns {Object} Default overhead data
 */
const getDefaultOverheadData = () => {
  return {
    expenses: {
      'Operational Overhead': [
        { description: 'Rent', amount: 0, frequency: 'Monthly' },
        { description: 'Utilities', amount: 0, frequency: 'Monthly' },
        { description: 'Office Supplies', amount: 0, frequency: 'Quarterly' },
        { description: 'Marketing & Advertising', amount: 0, frequency: 'Monthly' },
        { description: 'Professional Services', amount: 0, frequency: 'Annual' },
        { description: 'Office Maintenance & Cleaning', amount: 0, frequency: 'Monthly' }
      ],
      'Operation Payroll': [
        { description: 'Salaries & Wages', amount: 0, frequency: 'Annual' },
        { description: 'Payroll Taxes & Benefits', amount: 0, frequency: 'Annual' },
        { description: 'Workers\' Compensation', amount: 0, frequency: 'Annual' },
        { description: 'Bonuses & Incentives', amount: 0, frequency: 'Quarterly' }
      ],
      'Asset Payments': [
        { description: 'Ford F150', amount: 0, frequency: 'Monthly' },
        { description: 'Skid Steer', amount: 0, frequency: 'Monthly' }
      ],
      'Subscriptions': [
        { description: 'Tech Stack', amount: 0, frequency: 'Annual' },
        { description: 'Industry Memberships', amount: 0, frequency: 'Annual' },
        { description: 'Training & Education', amount: 0, frequency: 'Monthly' }
      ],
      'Insurance': [
        { description: 'General Liability Insurance', amount: 0, frequency: 'Annual' },
        { description: 'Workers\' Comp Insurance', amount: 0, frequency: 'Annual' },
        { description: 'Health Insurance Contributions', amount: 0, frequency: 'Monthly' },
        { description: 'Vehicle Insurance', amount: 0, frequency: 'Annual' }
      ],
      'Debt Service': [
        { description: 'Business Loan Repayments', amount: 0, frequency: 'Monthly' },
        { description: 'Credit Card Payments', amount: 0, frequency: 'Monthly' }
      ],
      'Soft Debt': [
        { description: 'Internal Loans', amount: 0, frequency: 'Monthly' }
      ]
    },
    timePeriod: 'Annual',
    summaryViewMode: 'box',
    // 'detailed' = itemized expense tables; 'quick' = one fixed total typed in
    inputMode: 'detailed',
    quickOverhead: { amount: 0, frequency: 'Monthly' }
  };
}; 